<template>
    <div class="add-show-plans">
        <base-loader
            v-if="! plans"
        />

        <template v-else>
            <div class="intro container">
                <h2>{{ modalTitle }}</h2>
                <p class="small description">
                    You’re almost done! Pick the plan that works for you.
                </p>
            </div>

            <div class="plans">
                <div
                    v-for="(plan) in plansForColumns"
                    :key="plan.id"
                    :class="[
                        'plan',
                        { highlight: hasHighlight(plan) },
                    ]"
                >
                    <svg-vue
                        v-if="planIcons[plan.slug]"
                        :icon="planIcons[plan.slug]"
                        class="plan-image"
                    />

                    <h3 class="plan-title">
                        {{ plan.name }}
                    </h3>

                    <div v-if="plan.minimalPrice" class="plan-pricing">
                        <p class="starting-at">
                            Starting at:
                        </p>
                        <p class="price-wrap">
                            <span class="price">{{ usdFormatter(plan.minimalPrice.amount) }}</span>
                            /mo
                        </p>
                    </div>

                    <plan-features :features="plan.features" />

                    <p v-if="isCurrent(plan)" class="current-plan-info">
                        Your Current Plan
                    </p>

                    <update-subscription-handler
                                v-else
                        v-slot:default="{ decrementSubscriptionProducts }"
                    >
                        <base-form-button
                            :class="[
                                'btn',
                                'plan-button',
                                {
                                    'btn-red': data.mode === 'downgrade',
                                    'btn-green': data.mode === 'upgrade',
                                }
                            ]"
                            :disabled="isLoading"
                            @click.native.prevent="selectPlan(plan, decrementSubscriptionProducts)"
                        >
                            {{ getButtonLabel(plan) }}
                        </base-form-button>
                    </update-subscription-handler>
                </div>
            </div>
        </template>
</div>
</template>
<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import PlanFeatures from '@/components/Shows/PlanFeatures';
import UpdateSubscriptionHandler from '@/components/Dashboard/Settings/Billing/UpdateSubscriptionHandler';
import usdFormatter from '@/utils/usdFormatter';

export default {
    components: {
        PlanFeatures,
        UpdateSubscriptionHandler,
    },

    props: {
        data: {
            type: [Object],
            default: () => ({}),
        },
    },

    data: () => ({
        isLoading: false,
        planIcons: {
            starter: 'starter-plan',
            pro: 'pro-plan',
        },
    }),

    computed: {
        ...mapState([
            'planFeatures',
            'shows',
            'user',
            'userSubscription',
        ]),
        ...mapState({
            /**
             * Returns filtered plans (available to upgrade from current plan).
             *
             * @param {object} state Vuex state.
             * @returns {Array} Available plans.
             */
            plans(state) {
                if (this.data.plan) {
                    return [this.data.plan];
                }

                if (this.data.planSlug) {
                    const plan = state.billingPlans?.find(
                        ({ slug }) => slug === this.data.planSlug
                    );

                    if (plan) {
                        return [plan];
                    }
                }

                return this.data.show?.plan
                    ? state.billingPlans?.filter(
                        ({ slug }) => this.data.show.plan[
                            'downgrade' === this.data.mode
                                ? 'downgradePlans'
                                : 'upgradePlans'
                        ].includes(slug)
                    )
                    : state.billingPlans;
            },
        }),

        /**
         * Returns plans to display in columns.
         *
         * @returns {Array} Available paid plans.
         */
        plansForColumns() {
            if (this.data.show?.plan && 1 === this.plans?.length) {
                const plans = this.plans;

                plans[
                    'downgrade' === this.data.mode ? 'push' : 'unshift'
                ](this.data.show?.plan);

                return plans;
            }

            return this.plans?.filter((plan) => ! plan.isFree);
        },

        /**
         * Determines if there is current plan on the show.
         *
         * @returns {boolean} Whether or not there is current plan on the show.
         */
        hasCurrentPlan() {
            return !! this.data.show?.plan &&
                !! this.plansForColumns.find(
                    ({ id }) => id === this.data.show.plan.id
                );
        },

        /**
         * Returns modal title based on current action.
         *
         * @returns {string} Modal title.
         */
        modalTitle() {
            if ('upgrade' === this.data.mode) {
                return this.data.plan
                    ? `Upgrade Your Plan to ${this.data.plan.name}`
                    : 'Upgrade Your Plan';
            }

            if ('downgrade' === this.data.mode) {
                return this.data.plan
                    ? `Sure you want to downagrade your ${this.data.show?.plan?.name} Plan to ${this.data.plan.name}?`
                    : `Sure you want to downagrade your ${this.data.show?.plan?.name} Plan`;
            }

            return 'Choose Your Podcasting Tools';
        },
    },

    methods: {
        ...mapActions(['convertShow']),
        ...mapMutations(['changeUIState']),

        usdFormatter,

        /**
         * Handles plan selecting.
         *
         * @param {object}   plan      Selected planId.
         * @param {Function} decrement Decrement callback.
         */
        async selectPlan(plan, decrement) {
            this.$emit('proceed', {
                ...this.data,
                plan,
            });
        },

        /**
         * Returns plan feature class.
         *
         * @param {object} feature      Feature object.
         * @param {Array}  planFeatures Plan features array.
         * @returns {object} Object with classnames.
         */
        getPlanFeatureClass(feature, planFeatures) {
            const hasFeature = feature.key
                ? planFeatures.includes(feature.key)
                : !! feature.find(({ key }) => planFeatures.includes(key));

            return {
                'plan-feature': true,
                enabled: hasFeature,
                disabled: ! hasFeature,
            };
        },

        /**
         * Returns plan feature class.
         *
         * @param {object} feature      Feature object.
         * @param {Array}  planFeatures Plan features array.
         * @returns {string} Plan feature name.
         */
        getPlanFeature(feature, planFeatures) {
            return feature.description
                ? feature.description
                : feature.find(({ key }) =>
                    !! planFeatures.includes(key))?.description ||
                    feature[0]?.description;
        },

        /**
         * Determines if given plan is current.
         *
         * @param {object} plan Plan object.
         * @returns {boolean}   Whether or not the plan is current.
         */
        isCurrent(plan) {
            return plan.id === this.data.show?.plan?.id;
        },

        /**
         * Determines if the plan should be highlighted. During upgrade, the new
         * plan (the one user is about to upgrade to) gets highlighted. Current
         * user plan gets highlighted while downgrading.
         *
         * @param {object} plan Selected planId.
         * @returns {boolean} Whether the plan should be highlighted or not.
         */
        hasHighlight(plan) {
            return 'downgrade' === this.data.mode
                ? this.isCurrent(plan)
                : this.hasCurrentPlan && ! this.isCurrent(plan);
        },

        /**
         * Determines if given plan is current.
         *
         * @param {object} plan Plan object.
         * @returns {boolean}   Whether or not the plan is current.
         */
        getButtonLabel(plan) {
            return 'upgrade' === this.data.mode
                ? `Upgrade to ${plan.name} Plan`
                : 'downgrade' === this.data.mode
                    ? `Yes, Downgrade to ${plan.name}`
                    : `Choose ${plan.name} Plan`;
        },
    },
};
</script>
